import React, { useState } from "react";

import Collapse from "../Collapse";

// Statuses are shown exactly as SoapUI reports them, only the colouring is ours.
const STEP_STYLES = {
  FAILED: { color: "#a61d15", background: "#f6f1f1" },
  CANCELED: { color: "#a61d15", background: "#f6f1f1" },
  OK: { color: "#1a7a4c", background: "#f1f6f3" },
  UNKNOWN: { color: "#5b5b5b", background: "#f4f4f4" },
};

const stepStyle = (step) => {
  if (step.failed) return STEP_STYLES.FAILED;
  return STEP_STYLES[step.status] || STEP_STYLES.UNKNOWN;
};

const formatDuration = (ms) => {
  if (ms === null || ms === undefined) return null;
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1).replace(".", ",")} s`;
};

/**
 * Renders one executed SoapUI test step: its status, how long it took, the HTTP status,
 * any SOAP fault, the individual assertion outcomes and the request/response payloads.
 * Failing steps are expanded by default so the user immediately sees why a test case failed.
 */
const TestStep = ({ step, testCase, onShowContent }) => {
  const [isExpanded, setIsExpanded] = useState(!!step.failed);
  const style = stepStyle(step);
  const duration = formatDuration(step.timeTakenMs);
  const failedAssertions = (step.assertions || []).filter((a) => a.status === "FAILED");

  return (
    <li
      style={{
        listStyle: "none",
        borderLeft: `4px solid ${style.color}`,
        backgroundColor: style.background,
        borderRadius: "0.25rem",
        padding: "0.75rem",
        marginBottom: "0.5rem",
      }}
    >
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 0,
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          textAlign: "left",
          width: "100%",
        }}
      >
        <span
          className={isExpanded ? "ids-icon-chevron-down" : "ids-icon-chevron-right"}
          aria-hidden="true"
          style={{ height: "1rem", width: "1rem" }}
        />
        <span style={{ fontWeight: 600 }}>
          {`Step ${step.index + 1}: ${step.name}`}
        </span>
        <span style={{ color: style.color, fontWeight: 600 }}>{step.status || "UNKNOWN"}</span>
        {duration && <span style={{ color: "#5b5b5b" }}>{duration}</span>}
        {step.httpStatus ? (
          <span style={{ color: step.httpStatus >= 400 ? style.color : "#5b5b5b" }}>
            {`HTTP ${step.httpStatus}`}
          </span>
        ) : null}
      </button>

      <Collapse open={isExpanded}>
        <div className="ids-mt-3" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {step.fault && (
            <div style={{ marginBottom: "0.5rem" }}>
              <strong>SOAP Fault</strong>
              {step.fault.code && (
                <div>
                  <strong>faultcode:</strong> {step.fault.code}
                </div>
              )}
              {step.fault.reason && (
                <div>
                  <strong>faultstring:</strong> {step.fault.reason}
                </div>
              )}
              {step.fault.detail && (
                <div style={{ marginTop: "0.25rem" }}>
                  <strong>detail:</strong>
                  <pre
                    style={{
                      margin: "0.25rem 0 0",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontFamily: "inherit",
                    }}
                  >
                    {step.fault.detail}
                  </pre>
                </div>
              )}
            </div>
          )}

          {step.error && (
            <p style={{ margin: "0 0 0.5rem" }}>
              <strong>Error:</strong> {step.error}
            </p>
          )}

          {(step.messages || []).length > 0 && (
            <div style={{ marginBottom: "0.5rem" }}>
              <strong>SoapUI messages</strong>
              <ul style={{ margin: "0.25rem 0 0 1rem" }}>
                {step.messages.map((message, i) => (
                  <li key={i}>{message}</li>
                ))}
              </ul>
            </div>
          )}

          {(step.assertions || []).length > 0 && (
            <div style={{ marginBottom: "0.5rem" }}>
              <strong>{`Assertions (${failedAssertions.length}/${step.assertions.length} failed)`}</strong>
              <ul style={{ margin: "0.25rem 0 0 1rem" }}>
                {step.assertions.map((assertion, i) => (
                  <li key={i} style={{ marginBottom: "0.25rem" }}>
                    <span
                      style={{
                        color: assertion.status === "FAILED" ? STEP_STYLES.FAILED.color : "#5b5b5b",
                        fontWeight: assertion.status === "FAILED" ? 600 : 400,
                      }}
                    >
                      {`${assertion.name}: ${assertion.status}`}
                    </span>
                    {assertion.error && (
                      <div style={{ marginTop: "0.15rem" }}>{assertion.error}</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(step.requestContent || step.responseContent || step.logs?.length > 0 || step.logs) && (
            <div className="ids-flex" style={{ gap: "1.5rem", flexWrap: "wrap" }}>
              {step.requestContent && (
                <button
                  type="button"
                  className="ids-link"
                  onClick={() =>
                    onShowContent({
                      title: `Request - ${testCase} / ${step.name}`,
                      content: step.requestContent,
                    })
                  }
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}
                >
                  Show request
                </button>
              )}
              {step.responseContent && (
                <button
                  type="button"
                  className="ids-link"
                  onClick={() =>
                    onShowContent({
                      title: `Response - ${testCase} / ${step.name}`,
                      content: step.responseContent,
                    })
                  }
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}
                >
                  Show response
                </button>
              )}
              {step.logs && (Array.isArray(step.logs) ? step.logs.length > 0 : step.logs) && (
                <button
                  type="button"
                  className="ids-link"
                  onClick={() =>
                    onShowContent({
                      title: `Loggar - ${testCase} / ${step.name}`,
                      content: Array.isArray(step.logs) ? step.logs.join("\n") : step.logs,
                    })
                  }
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}
                >
                  Se loggar
                </button>
              )}
            </div>
          )}
        </div>
      </Collapse>
    </li>
  );
};

/**
 * Lists the executed test steps of a test case so the user can see exactly which step failed,
 * which assertion did not hold and what the service answered.
 */
const TestStepDetails = ({ steps, testCase, onShowContent }) => {
  if (!Array.isArray(steps) || steps.length === 0) {
    return null;
  }

  const failedCount = steps.filter((step) => step.failed).length;

  return (
    <div className="ids-mt-4">
      <h4 className="ids-heading-xs" style={{ marginBottom: "0.5rem" }}>
        {failedCount > 0
          ? `Test steps (${failedCount}/${steps.length} failed)`
          : `Test steps (${steps.length})`}
      </h4>
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {steps.map((step) => (
          <TestStep
            key={step.index}
            step={step}
            testCase={testCase}
            onShowContent={onShowContent}
          />
        ))}
      </ul>
    </div>
  );
};

export default TestStepDetails;
